import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import NotificationBell from "./NotificationBell";
import LanguagePicker from "./LanguagePicker";
import {
  AirplaneTilt, GaugeIcon, CalendarBlank, NotePencil, CurrencyEur,
  ShieldCheck, Users, FileText, Wrench, ChartBar, ListChecks, SignOut, Coins, Gear, List, X, Shield, CreditCard,
  Lifebuoy, Lock
} from "@phosphor-icons/react";

const NavItem = ({ to, icon: Icon, label, testId, onClick }) => (
  <NavLink
    to={to}
    end
    data-testid={testId}
    onClick={onClick}
    className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 transition-colors duration-150 ${
        isActive
          ? "border-amber bg-[#1F1F1F] text-zinc-100"
          : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-[#141414]"
      }`
    }
  >
    <Icon size={18} weight="regular" />
    <span className="tracking-wide">{label}</span>
  </NavLink>
);

/** A Pro-only nav item shown to Standard tenants. Routes to /billing
 * and surfaces a lock icon + "PRO" pill on hover. */
const LockedNavItem = ({ icon: Icon, label, testId, onClick, navigate }) => (
  <button
    type="button"
    onClick={() => { onClick?.(); navigate("/billing?upgrade=cost_sharing"); }}
    data-testid={testId}
    className="group w-full flex items-center gap-3 px-4 py-2.5 text-sm border-l-2 border-transparent text-zinc-500 hover:text-amber hover:bg-[#141414] transition-colors duration-150 cursor-pointer"
    title="Included in OwnerCAM Pro — click to upgrade"
  >
    <Icon size={18} weight="regular" />
    <span className="tracking-wide flex-1 text-left">{label}</span>
    <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 border border-amber/40 text-amber bg-amber/5 group-hover:bg-amber/10">
      <Lock size={9} weight="fill" /> Pro
    </span>
  </button>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isOwner = user?.role === "owner" || user?.role === "tenant_admin" || user?.platform_superuser;
  const isSuper = !!user?.platform_superuser;
  const hasCostShare = !!user?.has_cost_sharing_access || !!user?.platform_superuser;
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeMobile = () => setMobileOpen(false);

  // Close drawer on route change
  React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-zinc-100">
      {/* Mobile top bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 h-14 bg-[#0A0A0A] border-b border-zinc-800 flex items-center justify-between px-4 print:hidden" data-testid="mobile-topbar">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2">
          <div className="w-7 h-7 border border-amber flex items-center justify-center">
            <AirplaneTilt size={14} weight="bold" className="text-amber" />
          </div>
          <span className="text-xs uppercase tracking-[0.2em] text-zinc-400">Private GA</span>
        </button>
        <div className="flex items-center gap-2">
          <LanguagePicker />
          <NotificationBell />
          <button onClick={() => setMobileOpen((v) => !v)} aria-label="Open menu" data-testid="mobile-menu-toggle" className="text-zinc-400 p-2">
            {mobileOpen ? <X size={22} /> : <List size={22} />}
          </button>
        </div>
      </div>

      {/* Drawer overlay (mobile) */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/70 backdrop-blur-sm" onClick={closeMobile} />
      )}

      {/* Sidebar — fixed drawer on mobile, static on lg+ */}
      <aside
        data-testid="sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 shrink-0 border-r border-zinc-800 flex flex-col bg-[#0A0A0A] transform transition-transform duration-200 lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
      >
        <div className="p-5 border-b border-zinc-800 hidden lg:flex items-center justify-between gap-3">
          <button onClick={() => navigate("/dashboard")} className="flex items-center gap-3 group flex-1 min-w-0" data-testid="sidebar-brand-btn">
            <div className="w-9 h-9 border border-amber flex items-center justify-center group-hover:bg-amber transition-colors shrink-0">
              <AirplaneTilt size={20} weight="bold" className="text-amber group-hover:text-black" />
            </div>
            <div className="text-left leading-tight min-w-0">
              <div className="text-sm font-semibold tracking-tight truncate">OwnerCAM</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 truncate">Aircraft ownership platform</div>
            </div>
          </button>
          <NotificationBell />
        </div>
        <div className="hidden lg:flex items-center gap-2 px-5 pb-3">
          <LanguagePicker />
        </div>
        <div className="lg:hidden h-14 flex items-center px-5 border-b border-zinc-800" />

        <nav className="flex-1 py-3 overflow-y-auto">
          <div className="px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-zinc-600">Operations</div>
          {/* ── OwnerCAM IA (Phase 4D nav rename) ── */}
          {isOwner && <NavItem to="/dashboard" icon={GaugeIcon} label="Dashboard" testId="nav-dashboard" onClick={closeMobile} />}
          {isOwner && <NavItem to="/fleet" icon={AirplaneTilt} label="Aircraft" testId="nav-fleet" onClick={closeMobile} />}
          <NavItem to="/logbook" icon={NotePencil} label="Flight Log" testId="nav-logbook" onClick={closeMobile} />
          <NavItem to="/maintenance" icon={Wrench} label="Maintenance" testId="nav-maintenance" onClick={closeMobile} />
          <NavItem to="/maintenance/fleet" icon={Wrench} label="Fleet maintenance" testId="nav-fleet-maintenance" onClick={closeMobile} />
          <NavItem to="/compliance" icon={ListChecks} label="Parts & Components" testId="nav-parts-components" onClick={closeMobile} />
          <NavItem to="/airworthiness" icon={ShieldCheck} label="ADs & Compliance" testId="nav-ad-compliance" onClick={closeMobile} />
          <NavItem to="/documents" icon={FileText} label="Documents" testId="nav-documents" onClick={closeMobile} />
          <NavItem to="/calendar" icon={CalendarBlank} label="Calendar" testId="nav-calendar" onClick={closeMobile} />

          <div className="px-4 py-2 mt-4 text-[10px] uppercase tracking-[0.25em] text-zinc-600">
            {hasCostShare ? "Cost Sharing · Pro" : "Cost Sharing · Pro"}
          </div>
          {hasCostShare ? (
            <>
              <NavItem to="/settlements" icon={CurrencyEur} label="Settlements" testId="nav-settlements" onClick={closeMobile} />
              <NavItem to="/reserves" icon={Coins} label="Reserves" testId="nav-reserves" onClick={closeMobile} />
              <NavItem to="/reconciliation" icon={ChartBar} label="Reports" testId="nav-reconciliation" onClick={closeMobile} />
            </>
          ) : (
            <>
              <LockedNavItem icon={CurrencyEur} label="Settlements" testId="nav-settlements-locked" onClick={closeMobile} navigate={navigate} />
              <LockedNavItem icon={Coins} label="Reserves" testId="nav-reserves-locked" onClick={closeMobile} navigate={navigate} />
              <LockedNavItem icon={ChartBar} label="Reports" testId="nav-reconciliation-locked" onClick={closeMobile} navigate={navigate} />
            </>
          )}

          {/* Phase 5 — Billing accessible to all members (any participant can manage subscription) */}
          <NavItem to="/billing" icon={CreditCard} label="Billing" testId="nav-billing" onClick={closeMobile} />

          {isOwner && (
            <>
              <div className="px-4 py-2 mt-4 text-[10px] uppercase tracking-[0.25em] text-zinc-600">Admin</div>
              <NavItem to="/participants" icon={Users} label="Participants" testId="nav-participants" onClick={closeMobile} />
              <NavItem to="/settings" icon={Gear} label="Settings" testId="nav-settings" onClick={closeMobile} />
              <NavItem to="/audit" icon={Lifebuoy} label="Audit log" testId="nav-audit" onClick={closeMobile} />
              {isSuper && <NavItem to="/superuser" icon={Shield} label="Superuser" testId="nav-superuser" onClick={closeMobile} />}
            </>
          )}
        </nav>

        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 border border-zinc-700 object-cover" />
            ) : (
              <div className="w-9 h-9 border border-zinc-700 bg-zinc-800" />
            )}
            <div className="leading-tight min-w-0">
              <div className="text-sm truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {user?.role} · <span className={user?.status === "active" ? "text-green-400" : "text-amber"}>{user?.status}</span>
              </div>
            </div>
          </div>
          <button
            onClick={logout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-amber py-2 border border-zinc-800 hover:border-amber transition-colors"
          >
            <SignOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto pt-14 lg:pt-0">
        <Outlet />
      </main>
    </div>
  );
}
